<template>
  <div ref="moveCursor" class="cursor"></div>
  <div ref="touchRipple" class="touch-ripple"></div>
</template>

<script setup>
import { gsap } from "gsap";
import { onMounted, onUnmounted, ref } from "vue";
import { store } from '../../store/index';
import { cursorGrowing } from "@/assets/global/global";

const moveCursor = ref(null);
const touchRipple = ref(null);

let animate = null;

function onTouchStart(e) {
  const touch = e.touches[0];
  const el = touchRipple.value;
  if (!el) return;
  el.style.left = touch.clientX + 'px';
  el.style.top = touch.clientY + 'px';
  el.classList.remove('ripple-active');
  void el.offsetWidth; // force reflow to restart animation
  el.classList.add('ripple-active');
}

onMounted(() => {
  if (window.innerWidth <= 1024) {
    moveCursor.value.setAttribute("style", "display:none");
    // Mobile: attach touch ripple instead
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    return;
  }

  cursorGrowing();
  if (!store.mouseGrow) {
    moveCursor.value.setAttribute("style", "display:none");
    return;
  }

  let mouseX = 0;
  let mouseY = 0;

  animate = gsap.to({}, 0.02, {
    repeat: -1,
    onRepeat() {
      gsap.set(moveCursor.value, { css: { left: mouseX, top: mouseY } });
    },
  });

  window.addEventListener("mousemove", (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });
});

onUnmounted(() => {
  document.removeEventListener('touchstart', onTouchStart);
  if (animate) {
    if (animate.scrollTrigger) animate.scrollTrigger.kill();
    animate.kill();
  }
});
</script>

<style lang="scss">
.cursor {
  position: fixed;
  width: 22px;
  height: 22px;
  z-index: 999999;
  pointer-events: none;
  border-radius: 50%;
  transform-origin: center center;
  transition: transform 0.5s ease-in-out, border-color 0.15s ease, box-shadow 0.15s ease;
  margin-left: -11px;
  margin-top: -11px;

  /* Circuit board pattern — same L-traces as hero fill effect */
  background-color: rgba(0, 200, 255, 0.05);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cpolyline points='0,18 15,18 15,4' fill='none' stroke='%23c8f0ff' stroke-width='1.5' stroke-linecap='round' stroke-opacity='0.75'/%3E%3Ccircle cx='15' cy='18' r='2' fill='%2300c8ff' fill-opacity='0.9'/%3E%3Cpolyline points='38,0 38,28 60,28' fill='none' stroke='%23c8f0ff' stroke-width='1.5' stroke-linecap='round' stroke-opacity='0.75'/%3E%3Ccircle cx='38' cy='28' r='2' fill='%2300c8ff' fill-opacity='0.9'/%3E%3Cpolyline points='5,50 24,50 24,60' fill='none' stroke='%23c8f0ff' stroke-width='1.5' stroke-linecap='round' stroke-opacity='0.75'/%3E%3Ccircle cx='24' cy='50' r='2' fill='%2300c8ff' fill-opacity='0.9'/%3E%3Ccircle cx='52' cy='12' r='1.5' fill='%23c8f0ff' fill-opacity='0.45'/%3E%3Ccircle cx='7' cy='38' r='1.5' fill='%23c8f0ff' fill-opacity='0.45'/%3E%3Ccircle cx='48' cy='52' r='1.5' fill='%23c8f0ff' fill-opacity='0.45'/%3E%3C/svg%3E");
  background-size: 44px 44px;

  border: 1.5px solid rgba(0, 200, 255, 0.7);
  box-shadow: 0 0 8px rgba(0, 200, 255, 0.35), inset 0 0 5px rgba(0, 200, 255, 0.08);

  /* Slow drift — circuit pattern flows sideways */
  animation: circuitCursorDrift 4s linear infinite;

  @media (max-width: 1024px) {
    display: none;
  }
}

@keyframes circuitCursorDrift {
  0%   { background-position: 0 0; }
  100% { background-position: 44px 0; }
}

.grow,
.grow-small {
  transform: scale(1.8);
}

.grow-small {
  transform: scale(1);
}

.scale {
  cursor: none;
}

/* ─── Mobile touch ripple ─────────────────────────── */
.touch-ripple {
  position: fixed;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 200, 255, 0.85);
  transform: translate(-50%, -50%) scale(0);
  pointer-events: none;
  z-index: 999998;
  opacity: 0;

  @media (min-width: 1025px) {
    display: none;
  }

  &.ripple-active {
    animation: touchRippleAnim 0.55s ease-out forwards;
  }
}

@keyframes touchRippleAnim {
  0%   { transform: translate(-50%, -50%) scale(0);   opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(3.5); opacity: 0; }
}
</style>
